import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  open: boolean;
  title: string;
  loading: boolean;
  error: string | null;
  fileName: string | null;
  fileBase64: string | null; // Base64 content z backendu
  onClose: () => void;
  onDownload: () => void;
};

export default function DocumentPreviewModal({
  open,
  title,
  loading,
  error,
  fileName,
  fileBase64,
  onClose,
  onDownload,
}: Props) {
  const { t } = useAppTranslation("common");
  const [textContent, setTextContent] = useState<string>("");

  const getFileExtension = (name: string | null): string => {
    if (!name) return "";
    return name.toLowerCase().split(".").pop() || "";
  };

  useEffect(() => {
    if (fileBase64 && fileName) {
      const extension = getFileExtension(fileName);
      
      // Dekoduj XML lub TXT z Base64
      if (["txt", "xml"].includes(extension)) {
        try {
          const decoded = atob(fileBase64);
          setTextContent(decoded);
        } catch (e) {
          console.error("Błąd dekodowania Base64:", e);
          setTextContent(t("Błąd dekodowania pliku"));
        }
      }
    }
  }, [fileBase64, fileName, t]);

  const renderPreview = () => {
    if (!fileName || !fileBase64) {
      return (
        <Typography color="text.secondary" align="center">
          {t("Brak danych do wyświetlenia")}
        </Typography>
      );
    }

    const extension = getFileExtension(fileName);

    // Obrazy
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(extension)) {
      const mimeType = extension === "jpg" ? "jpeg" : extension;
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <img
            src={`data:image/${mimeType};base64,${fileBase64}`}
            alt={fileName}
            style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
          />
        </Box>
      );
    }

    // PDF
    if (extension === "pdf") {
      return (
        <iframe
          title="PDF preview"
          src={`data:application/pdf;base64,${fileBase64}`}
          style={{ width: "100%", height: "70vh", border: "none" }}
        />
      );
    }

    // Pliki tekstowe i XML
    if (["txt", "xml"].includes(extension)) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "70vh",
            overflow: "auto",
            backgroundColor: "#f5f5f5",
            border: "1px solid #ccc",
            p: 2,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            fontSize: "14px",
            wordBreak: "break-word",
          }}
        >
          {textContent || t("Ładowanie zawartości...")}
        </Box>
      );
    }

    // Word - brak podglądu
    if (["doc", "docx"].includes(extension)) {
      return (
        <Box textAlign="center" p={3}>
          <Typography color="text.secondary" gutterBottom>
            {t("Podgląd plików Word nie jest dostępny.")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Użyj przycisku "Pobierz", aby otworzyć plik w aplikacji Word.')}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography color="text.secondary" align="center">
        {t("Podgląd nie jest dostępny dla plików typu .{{extension}}", { extension })}
      </Typography>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" align="center">
            {error}
          </Typography>
        )}

        {!loading && !error && renderPreview()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onDownload} variant="contained">
          {t("Pobierz")}
        </Button>
        <Button onClick={onClose}>
          {t("Zamknij")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}